import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Checkbox } from '../ui/checkbox';
import { 
  Maximize2, Minimize2, Cloud, CloudOff, 
  ChevronDown, ChevronUp, RotateCcw, ExternalLink, Eye, EyeOff, Download
} from 'lucide-react';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import EbookPageFooter from '../shared/EbookPageFooter';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';
import {
  FeelIcon, NeedIcon, GoldIcon,
  ExerciseIcon, HomeworkIcon, PartnerIcon,
  VideoIcon, ReadingIcon, SaveIcon, ChapterIcon,
  ArrowRightIcon, ArrowLeftIcon, CheckIcon, InsightIcon
} from '../icons/ElchmeIcons';
import { downloadExercisePDF, generateChapter4Content } from '../../utils/pdfDownload';

const CHAPTER_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?w=1200&q=80',
  needs: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80',
  connection: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80',
  celebration: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80',
  reflection: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
};

// Exercise 1 prompts - Page 109
const exercise1Prompts = [
  { id: 1, prompt: "I love...", example: null },
  { id: 2, prompt: "I value...", example: null },
  { id: 3, prompt: "I need...", example: null },
  { id: 4, prompt: "I long for...", example: null },
  { id: 5, prompt: "I deeply desire...", example: null },
  { id: 6, prompt: "I am happiest when I...", example: null },
  { id: 7, prompt: "I am fulfilled by...", example: null },
  { id: 8, prompt: "I am inspired when...", example: null },
  { id: 9, prompt: "I feel the most alive when...", example: null },
  { id: 10, prompt: "It is important for me to...", example: null },
];

// Exercise 3 data with answers - Pages 110-112
const exercise3Data = [
  { 
    id: 1, 
    statement: "You are such a terrible boss.",
    feelingPrefix: "I feel",
    needPrefix: "because I need",
    answer: { feeling: "irritated", need: "acknowledgment of my skills" }
  },
  { 
    id: 2, 
    statement: "I just don't understand why you are so different.",
    feelingPrefix: "I feel",
    needPrefix: "because I value",
    answer: { feeling: "uncomfortable", need: "consistency and predictability" }
  },
  { 
    id: 3, 
    statement: "We are late! It's time to go!",
    feelingPrefix: "I feel",
    needPrefix: "because I value",
    answer: { feeling: "impatient", need: "punctuality" }
  },
  { 
    id: 4, 
    statement: "I thought that you liked me.",
    feelingPrefix: "I feel",
    needPrefix: "because I need",
    answer: { feeling: "hurt", need: "connection" }
  },
  { 
    id: 5, 
    statement: "I can't stand it when you chew with your mouth open.",
    feelingPrefix: "I feel",
    needPrefix: "because I value",
    answer: { feeling: "disgusted", need: "certain table manners" }
  },
  { 
    id: 6, 
    statement: "You are a good daughter/son.",
    feelingPrefix: "I feel",
    needPrefix: "because my need for",
    needSuffix: "is being met",
    answer: { feeling: "grateful", need: "cooperation" }
  },
  { 
    id: 7, 
    statement: "I can do whatever I want. You can't boss me around.",
    feelingPrefix: "I feel",
    needPrefix: "because I am needing",
    answer: { feeling: "upset", need: "independence" }
  },
  { 
    id: 8, 
    statement: "Come on, I know you can do better than that.",
    feelingPrefix: "I feel",
    needPrefix: "because I need",
    answer: { feeling: "disappointed", need: "to support your growth" }
  },
  { 
    id: 9, 
    statement: "You did such a good job cleaning your room.",
    feelingPrefix: "I feel",
    needPrefix: "because I need",
    answer: { feeling: "thankful", need: "order and cleanliness" }
  },
  { 
    id: 10, 
    statement: "I wish someone would listen to me.",
    feelingPrefix: "I feel",
    needPrefix: "because I need",
    answer: { feeling: "fragile", need: "attention to my feelings" }
  },
  { 
    id: 11, 
    statement: "Whatever...",
    feelingPrefix: "I feel",
    needPrefix: "because I need",
    answer: { feeling: "frustrated", need: "effective communication" }
  },
  { 
    id: 12, 
    statement: "We should hang out more often.",
    feelingPrefix: "I feel",
    needPrefix: "because I need",
    answer: { feeling: "happy", need: "companionship" }
  },
];

// Common needs categories for Exercise 2
const needsCategories = [
  { category: 'CONNECTION', needs: ['acceptance', 'appreciation', 'belonging', 'closeness', 'communication', 'community', 'companionship', 'compassion', 'connection', 'consideration', 'empathy', 'inclusion', 'intimacy', 'love', 'mutuality', 'nurturing', 'respect', 'support', 'trust', 'understanding', 'warmth'] },
  { category: 'PHYSICAL WELL-BEING', needs: ['air', 'food', 'movement', 'rest', 'safety', 'shelter', 'touch', 'water'] },
  { category: 'HONESTY', needs: ['authenticity', 'integrity', 'presence', 'self-connection'] },
  { category: 'PLAY', needs: ['joy', 'humor', 'fun', 'relaxation', 'rejuvenation'] },
  { category: 'PEACE', needs: ['beauty', 'communion', 'ease', 'equality', 'harmony', 'inspiration', 'order'] },
  { category: 'AUTONOMY', needs: ['choice', 'freedom', 'independence', 'space', 'spontaneity'] },
  { category: 'MEANING', needs: ['awareness', 'celebration', 'challenge', 'clarity', 'competence', 'consciousness', 'contribution', 'creativity', 'discovery', 'effectiveness', 'growth', 'hope', 'learning', 'mourning', 'participation', 'purpose', 'self-expression', 'stimulation', 'understanding'] },
];

export default function Chapter4Presentation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSection = parseInt(searchParams.get('section')) || 0;
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState('ex1-1');
  const [showAnswers, setShowAnswers] = useState({});

  // Exercise 1 responses
  const [exercise1Responses, setExercise1Responses] = useState(() => {
    const saved = localStorage.getItem('chapter4Exercise1');
    return saved ? JSON.parse(saved) : {};
  });

  // Exercise 2 responses
  const [exercise2Relationship, setExercise2Relationship] = useState(() => {
    const saved = localStorage.getItem('chapter4Exercise2Relationship');
    return saved || '';
  });
  const [exercise2Needs, setExercise2Needs] = useState(() => {
    const saved = localStorage.getItem('chapter4Exercise2Needs');
    return saved ? JSON.parse(saved) : {};
  });
  const [exercise2Priority, setExercise2Priority] = useState(() => {
    const saved = localStorage.getItem('chapter4Exercise2Priority');
    return saved ? JSON.parse(saved) : {};
  });
  const [exercise2Met, setExercise2Met] = useState(() => {
    const saved = localStorage.getItem('chapter4Exercise2Met');
    return saved ? JSON.parse(saved) : {};
  });
  const [exercise2Unmet, setExercise2Unmet] = useState(() => {
    const saved = localStorage.getItem('chapter4Exercise2Unmet');
    return saved ? JSON.parse(saved) : {};
  });

  // Exercise 3 responses
  const [exercise3Responses, setExercise3Responses] = useState(() => {
    const saved = localStorage.getItem('chapter4Exercise3');
    return saved ? JSON.parse(saved) : {};
  });

  // Sections - with PDF page references
  const sections = [
    { id: 'intro', title: 'Chapter 4: Needs', page: 108 },
    { id: 'needs-definitions', title: 'Understanding Needs', page: 108 },
    { id: 'exercise1-intro', title: 'Exercise 1: Connecting to Your Needs', page: 109 },
    { id: 'exercise1-practice', title: 'Exercise 1: Practice', page: 109 },
    { id: 'exercise2-intro', title: 'Exercise 2: Relationship Needs', page: 109 },
    { id: 'exercise2-practice', title: 'Exercise 2: Practice', page: 109 },
    { id: 'exercise3-intro', title: 'Exercise 3: Translate to Feelings & Needs', page: 110 },
    { id: 'exercise3-practice', title: 'Exercise 3: Practice', page: 110 },
    { id: 'exercise3-answers', title: 'Exercise 3: Possible Answers', page: 112 },
    { id: 'celebration-reading', title: 'Information in Celebration', page: 111 },
    { id: 'homework', title: 'Chapter 4 Homework', page: 113 },
  ];

  const progress_pct = ((currentSection + 1) / sections.length) * 100;

  // Check login status
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Fetch progress from server
  const fetchUserProgress = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter4`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.exercise1Responses) {
          setExercise1Responses(data.exercise1Responses);
          localStorage.setItem('chapter4Exercise1', JSON.stringify(data.exercise1Responses));
        }
        if (data.exercise3Responses) {
          setExercise3Responses(data.exercise3Responses);
          localStorage.setItem('chapter4Exercise3', JSON.stringify(data.exercise3Responses));
        }
        setLastSyncTime(new Date());
      }
    } catch (error) {
      console.log('Could not fetch progress from server');
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchUserProgress();
    }
  }, [isLoggedIn, fetchUserProgress]);

  // Save progress
  const saveProgress = async () => {
    localStorage.setItem('chapter4Exercise1', JSON.stringify(exercise1Responses));
    localStorage.setItem('chapter4Exercise2Relationship', exercise2Relationship);
    localStorage.setItem('chapter4Exercise2Needs', JSON.stringify(exercise2Needs));
    localStorage.setItem('chapter4Exercise2Priority', JSON.stringify(exercise2Priority));
    localStorage.setItem('chapter4Exercise2Met', JSON.stringify(exercise2Met));
    localStorage.setItem('chapter4Exercise2Unmet', JSON.stringify(exercise2Unmet));
    localStorage.setItem('chapter4Exercise3', JSON.stringify(exercise3Responses));
    
    if (isLoggedIn) {
      setIsSyncing(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter4`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            exercise1Responses,
            exercise2Relationship,
            exercise2Needs,
            exercise2Priority,
            exercise2Met,
            exercise2Unmet,
            exercise3Responses
          })
        });
        if (response.ok) {
          setLastSyncTime(new Date());
          toast.success('Progress saved to your account!');
        }
      } catch (error) {
        toast.error('Saved locally (server sync failed)');
      } finally {
        setIsSyncing(false);
      }
    } else {
      toast.success('Saved locally! Sign in to sync across devices.');
    }
  };

  const resetAll = () => {
    if (window.confirm('Reset all Chapter 4 responses? This cannot be undone.')) {
      setExercise1Responses({});
      setExercise2Relationship('');
      setExercise2Needs({});
      setExercise2Priority({});
      setExercise2Met({});
      setExercise2Unmet({});
      setExercise3Responses({});
      localStorage.removeItem('chapter4Exercise1');
      localStorage.removeItem('chapter4Exercise2Relationship');
      localStorage.removeItem('chapter4Exercise2Needs');
      localStorage.removeItem('chapter4Exercise2Priority');
      localStorage.removeItem('chapter4Exercise2Met');
      localStorage.removeItem('chapter4Exercise2Unmet');
      localStorage.removeItem('chapter4Exercise3');
      toast.success('All responses reset!');
    }
  };

  // Download as PDF
  const downloadPDF = () => {
    const exercise2Data = {
      relationship: exercise2Relationship,
      needs: exercise2Needs,
      priority: exercise2Priority,
      met: exercise2Met,
      unmet: exercise2Unmet
    };
    const content = generateChapter4Content(exercise1Responses, exercise2Data, exercise3Responses);
    downloadExercisePDF(content, 'Chapter 4: Needs Exercises', 'chapter-4-exercises.pdf');
    toast.success('PDF download started! Use "Save as PDF" in print dialog.');
  };

  // Toggle presentation mode
  const togglePresentationMode = async () => {
    if (!isPresentationMode) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) { console.log('Fullscreen not available'); }
    } else {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch (e) { console.log('Exit fullscreen failed'); }
    }
    setIsPresentationMode(!isPresentationMode);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isPresentationMode) {
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setCurrentSection(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Escape') {
          setIsPresentationMode(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPresentationMode, sections.length]);

  // Pulsing Feelings & Needs CTA Button
  const FeelingsNeedsCTA = ({ className = '' }) => (
    <Button
      onClick={() => setIsHelperOpen(true)}
      className={`relative bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white ${className}`}
      data-testid="feelings-needs-cta"
    >
      <div className="absolute inset-0 rounded-md bg-rose-500/30 animate-ping" style={{ animationDuration: '2s' }} />
      <span className="relative flex items-center gap-2">
        <FeelIcon size={18} />
        Feelings & Needs Tool
        <NeedIcon size={18} />
      </span>
    </Button>
  );

  // Render content based on section
  const renderContent = () => {
    switch (currentSection) {
      // INTRO - Page 108
      case 0:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="chapter4-intro">
            <div className="relative rounded-xl overflow-hidden shadow-2xl h-64 lg:h-96">
              <img src={CHAPTER_IMAGES.hero} alt="Needs" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-8">
                <div>
                  <h2 className="text-4xl lg:text-5xl font-bold text-white mb-2">Chapter 4: Needs</h2>
                  <p className="text-xl text-emerald-200">Pages 108-113 • The Heart of Connection</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center">
              <FeelingsNeedsCTA className="px-6 py-3 text-lg" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-6">
                <NeedIcon size={32} className="mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Universal Needs</h3>
                <p className="text-slate-300">
                  Discover the <span className="text-emerald-300 font-semibold">universal aspects of life</span> that 
                  contribute to a healthy and full existence for everyone.
                </p>
              </div>
              <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-6">
                <GoldIcon size={32} className="mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Source of Action</h3>
                <p className="text-slate-300">
                  Understand how <span className="text-amber-300 font-semibold">everything we do</span> is an 
                  attempt to meet a need.
                </p>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300 text-lg">
                In this chapter, you'll learn to identify your <span className="text-emerald-400">deepest needs</span> and 
                understand how they drive every thought, feeling, and action.
              </p>
            </div>

            {/* Link to Page 158 */}
            <div className="bg-gradient-to-r from-amber-900/40 to-orange-900/30 border-2 border-amber-500/50 rounded-xl p-6 hover:border-amber-400 transition-colors cursor-pointer" onClick={() => navigate('/workbook/page-158')} data-testid="page158-callout">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-2xl">⭐</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-amber-300 font-bold text-lg mb-2">Related: Page 158 — Translating Judgments</h3>
                  <p className="text-slate-300 text-sm mb-3">
                    Learn to translate judgments, criticisms, and demands into feelings and needs. Discover Marshall's insight: 
                    <span className="text-amber-200 italic"> "Every judgment is a tragic expression of an unmet need."</span>
                  </p>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-white">
                    Explore Page 158 <ArrowRightIcon size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      // NEEDS DEFINITIONS - Page 108
      case 1:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="needs-definitions">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <h2 className="text-3xl font-bold text-white mb-2">Understanding "Needs" in NVC</h2>
                <p className="text-emerald-300 text-lg">Page 108</p>
              </div>
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-500/30 rounded-2xl p-8">
              <p className="text-lg text-slate-200 mb-6 italic">
                The word "needs" in the NVC context points to a complex and rich reality and is difficult to define. 
                Here are some definitions that attempt to capture its meaning:
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-slate-800/50 rounded-xl p-5">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <p className="text-slate-200">
                    Needs are the <span className="text-emerald-300 font-semibold">universal aspects of life</span> that 
                    contribute to a healthy and full existence for everyone and everything.
                  </p>
                </div>

                <div className="flex items-start gap-4 bg-slate-800/50 rounded-xl p-5">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <p className="text-slate-200">
                    Needs are the <span className="text-emerald-300 font-semibold">life force</span> that flows through 
                    us and the world in every moment.
                  </p>
                </div>

                <div className="flex items-start gap-4 bg-slate-800/50 rounded-xl p-5">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <p className="text-slate-200">
                    Needs are the <span className="text-emerald-300 font-semibold">ineffable building blocks</span> of life. 
                    Some refer to their totality as God.
                  </p>
                </div>

                <div className="flex items-start gap-4 bg-slate-800/50 rounded-xl p-5">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <p className="text-slate-200">
                    Needs are the <span className="text-emerald-300 font-semibold">source and impetus</span> behind 
                    every thought, feeling, and action.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-6">
              <h3 className="text-purple-300 font-bold mb-4 flex items-center gap-2">
                <InsightIcon size={20} color="#A855F7" />
                Function and Form
              </h3>
              <p className="text-slate-300 mb-4">Sometimes it's useful to talk about "needs" by discussing their function and form:</p>
              <ul className="space-y-3 text-slate-200">
                <li className="flex items-start gap-3">
                  <span className="text-purple-400">•</span>
                  <span><span className="text-purple-300 font-semibold">Everything</span> anyone ever does is an attempt to meet a need.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-400">•</span>
                  <span><span className="text-purple-300 font-semibold">Feelings arise</span> due to a need that is met or unmet.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-400">•</span>
                  <span><span className="text-purple-300 font-semibold">All of humanity</span> shares the same needs.</span>
                </li>
              </ul>
            </div>

            <div className="bg-rose-900/20 border border-rose-500/30 rounded-xl p-6 text-center">
              <p className="text-rose-200 italic">
                Please refer to the <span className="text-rose-300 font-semibold">Needs Inventory</span> for a possible list of human needs.
              </p>
              <Button
                onClick={() => setIsHelperOpen(true)}
                className="mt-4 bg-rose-600 hover:bg-rose-500"
              >
                <NeedIcon size={18} className="mr-2" />
                Open Needs Inventory
              </Button>
            </div>
          </div>
        );

      // EXERCISE 1 INTRO - Page 109
      case 2:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise1-intro">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
                  <ExerciseIcon size={40} color="#ffffff" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Exercise 1</h2>
                <p className="text-emerald-300 text-lg">Page 109 • Connecting to Your Needs</p>
              </div>
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <p className="text-slate-200 text-lg leading-relaxed">
                Thinking in general about your life and yourself, complete the sentences below. 
                After completing the sentence, go to the <span className="text-emerald-300 font-semibold">needs list</span> and 
                pick out the needs that are being met. Do as many of these as you want.
              </p>
            </div>

            <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
              <h3 className="text-blue-300 font-bold mb-4">💡 Example</h3>
              <div className="space-y-3">
                <p className="text-slate-300">
                  <span className="text-white font-semibold">I excel at...</span> making other people laugh.
                </p>
                <p className="text-slate-300">
                  <span className="text-emerald-400 font-semibold">Need(s) being met:</span> fun, play, connection
                </p>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <Button 
                onClick={() => setCurrentSection(3)}
                className="bg-white text-slate-900 hover:bg-slate-200 text-lg px-8 py-6 rounded-full"
              >
                Start Exercise <ArrowRightIcon size={24} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // EXERCISE 1 PRACTICE - Page 109
      case 3:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise1-practice">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Exercise 1: Connect to Your Needs</h2>
              <FeelingsNeedsCTA />
            </div>

            <p className="text-slate-400 text-sm text-center">Complete each prompt and identify the needs being met</p>

            <div className="space-y-4">
              {exercise1Prompts.map((item) => (
                <div key={item.id} className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedItem(expandedItem === `ex1-${item.id}` ? null : `ex1-${item.id}`)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="bg-emerald-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
                        {item.id}
                      </span>
                      <p className="text-slate-200 text-left font-medium">{item.prompt}</p>
                    </div>
                    {expandedItem === `ex1-${item.id}` ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                  
                  {expandedItem === `ex1-${item.id}` && (
                    <div className="px-5 pb-4 space-y-4 animate-in slide-in-from-top duration-200">
                      <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block">Your Response:</label>
                        <Textarea
                          value={exercise1Responses[item.id]?.response || ''}
                          onChange={(e) => setExercise1Responses(prev => ({ 
                            ...prev, 
                            [item.id]: { ...prev[item.id], response: e.target.value }
                          }))}
                          placeholder="Complete the sentence..."
                          className="bg-slate-900/50 border-slate-600 text-white min-h-[80px]"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-emerald-300 mb-2 block">Need(s) being met:</label>
                        <Textarea
                          value={exercise1Responses[item.id]?.needs || ''}
                          onChange={(e) => setExercise1Responses(prev => ({ 
                            ...prev, 
                            [item.id]: { ...prev[item.id], needs: e.target.value }
                          }))}
                          placeholder="e.g., connection, play, growth..."
                          className="bg-emerald-900/20 border-emerald-700/30 text-white min-h-[60px]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      // EXERCISE 2 INTRO - Page 109
      case 4:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise2-intro">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <div className="w-20 h-20 bg-gradient-to-r from-rose-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-rose-500/20">
                  <PartnerIcon size={40} color="#ffffff" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Exercise 2</h2>
                <p className="text-rose-300 text-lg">Page 109 • Relationship Needs</p>
              </div>
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Instructions</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <span className="bg-rose-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">1</span>
                  <p className="text-slate-200">Pick a relationship you want to use for this exercise (partner, friend, mother/father, children, boss).</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="bg-rose-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">2</span>
                  <p className="text-slate-200">Using your Needs Inventory, fill in the needs that are important to you in this relationship.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="bg-rose-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">3</span>
                  <p className="text-slate-200">Prioritize the top six by using a check mark. These are the needs/values you really want met.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="bg-rose-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">4</span>
                  <p className="text-slate-200">Circle the needs that ARE NOT being met in this relationship.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="bg-rose-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">5</span>
                  <p className="text-slate-200">Place a rectangle around the needs that ARE being met.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="bg-rose-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">6</span>
                  <p className="text-slate-200">Count how many of your needs are and are not being met. What does this reveal to you?</p>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <Button 
                onClick={() => setCurrentSection(5)}
                className="bg-white text-slate-900 hover:bg-slate-200 text-lg px-8 py-6 rounded-full"
              >
                Start Exercise <ArrowRightIcon size={24} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // EXERCISE 2 PRACTICE - Page 109
      case 5:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise2-practice">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Exercise 2: Relationship Needs</h2>
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <label className="text-sm font-medium text-rose-300 mb-2 block">Step 1: Choose a Relationship</label>
              <Input
                value={exercise2Relationship}
                onChange={(e) => setExercise2Relationship(e.target.value)}
                placeholder="e.g., Partner, Friend, Mother, Boss..."
                className="bg-slate-900/50 border-slate-600 text-white"
              />
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <label className="text-sm font-medium text-rose-300 mb-4 block">
                Steps 2-5: Select needs important to you in this relationship
              </label>
              <p className="text-slate-400 text-sm mb-4">
                ✓ = Priority (top 6) | 🔴 = Unmet | 🟢 = Met
              </p>
              
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {needsCategories.map((cat) => (
                  <div key={cat.category} className="bg-slate-900/50 rounded-lg p-4">
                    <h4 className="text-emerald-400 font-semibold text-sm mb-3">{cat.category}</h4>
                    <div className="flex flex-wrap gap-2">
                      {cat.needs.map((need) => {
                        const isSelected = exercise2Needs[need];
                        const isPriority = exercise2Priority[need];
                        const isMet = exercise2Met[need];
                        const isUnmet = exercise2Unmet[need];
                        
                        return (
                          <div 
                            key={need}
                            className={`relative group px-3 py-1.5 rounded-full text-sm cursor-pointer transition-all ${
                              isSelected 
                                ? isMet 
                                  ? 'bg-emerald-600/50 text-emerald-100 border-2 border-emerald-400'
                                  : isUnmet
                                    ? 'bg-rose-600/50 text-rose-100 border-2 border-rose-400'
                                    : 'bg-slate-600/50 text-slate-100 border-2 border-slate-400'
                                : 'bg-slate-700/30 text-slate-400 hover:bg-slate-600/50 hover:text-slate-200'
                            }`}
                            onClick={() => setExercise2Needs(prev => ({ ...prev, [need]: !prev[need] }))}
                          >
                            {isPriority && <span className="mr-1">✓</span>}
                            {need}
                            
                            {isSelected && (
                              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-600 rounded-lg p-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setExercise2Priority(prev => ({ ...prev, [need]: !prev[need] })); }}
                                  className={`w-6 h-6 rounded flex items-center justify-center text-xs ${isPriority ? 'bg-amber-600' : 'bg-slate-700 hover:bg-slate-600'}`}
                                  title="Priority"
                                >✓</button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setExercise2Met(prev => ({ ...prev, [need]: !prev[need] })); setExercise2Unmet(prev => ({ ...prev, [need]: false })); }}
                                  className={`w-6 h-6 rounded flex items-center justify-center text-xs ${isMet ? 'bg-emerald-600' : 'bg-slate-700 hover:bg-slate-600'}`}
                                  title="Met"
                                >🟢</button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setExercise2Unmet(prev => ({ ...prev, [need]: !prev[need] })); setExercise2Met(prev => ({ ...prev, [need]: false })); }}
                                  className={`w-6 h-6 rounded flex items-center justify-center text-xs ${isUnmet ? 'bg-rose-600' : 'bg-slate-700 hover:bg-slate-600'}`}
                                  title="Unmet"
                                >🔴</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-5">
              <label className="text-sm font-medium text-purple-300 mb-2 block">Step 6: What does this reveal to you?</label>
              <p className="text-slate-400 text-sm mb-3">
                Met: {Object.values(exercise2Met).filter(Boolean).length} | 
                Unmet: {Object.values(exercise2Unmet).filter(Boolean).length} | 
                Priority: {Object.values(exercise2Priority).filter(Boolean).length}
              </p>
              <Textarea
                placeholder="Reflect on what you've discovered about your needs in this relationship..."
                className="bg-purple-900/20 border-purple-700/30 text-white min-h-[100px]"
              />
            </div>
          </div>
        );

      // EXERCISE 3 INTRO - Page 110
      case 6:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise3-intro">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/20">
                  <ExerciseIcon size={40} color="#ffffff" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Exercise 3</h2>
                <p className="text-amber-300 text-lg">Page 110 • Translate to Feelings & Needs</p>
              </div>
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <p className="text-slate-200 text-lg leading-relaxed">
                Using the <span className="text-rose-300 font-semibold">feelings</span> and{' '}
                <span className="text-emerald-300 font-semibold">needs</span> list, translate the following 
                statements into feelings and needs.
              </p>
            </div>

            <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
              <h3 className="text-blue-300 font-bold mb-4">💡 Example</h3>
              <div className="space-y-3">
                <p className="text-slate-400">
                  <span className="text-amber-400 font-medium">Statement:</span>{' '}
                  <span className="text-white">"You should listen to me when I'm talking to you."</span>
                </p>
                <p className="text-slate-300">
                  <span className="text-emerald-400 font-medium">Translation:</span>{' '}
                  I feel <span className="text-rose-300 font-semibold">frustrated</span> because I need{' '}
                  <span className="text-emerald-300 font-semibold">respect</span> when I am speaking.
                </p>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <Button 
                onClick={() => setCurrentSection(7)}
                className="bg-white text-slate-900 hover:bg-slate-200 text-lg px-8 py-6 rounded-full"
              >
                Start Exercise <ArrowRightIcon size={24} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // EXERCISE 3 PRACTICE - Page 110
      case 7:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise3-practice">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Exercise 3: Translate to Feelings & Needs</h2>
              <FeelingsNeedsCTA />
            </div>

            <p className="text-slate-400 text-sm text-center">
              Translate each statement. Click "Show Answer" to reveal possible translations.
            </p>

            <div className="space-y-4">
              {exercise3Data.map((item) => (
                <div key={item.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="bg-amber-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      {item.id}
                    </span>
                    <p className="text-slate-200 text-lg font-medium">"{item.statement}"</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium text-rose-300 mb-2 block">{item.feelingPrefix}...</label>
                      <Input
                        value={exercise3Responses[item.id]?.feeling || ''}
                        onChange={(e) => setExercise3Responses(prev => ({
                          ...prev,
                          [item.id]: { ...prev[item.id], feeling: e.target.value }
                        }))}
                        placeholder="Enter a feeling..."
                        className="bg-rose-900/20 border-rose-700/30 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-emerald-300 mb-2 block">
                        {item.needPrefix}...{item.needSuffix ? ` (${item.needSuffix})` : ''}
                      </label>
                      <Input
                        value={exercise3Responses[item.id]?.need || ''}
                        onChange={(e) => setExercise3Responses(prev => ({
                          ...prev,
                          [item.id]: { ...prev[item.id], need: e.target.value }
                        }))}
                        placeholder="Enter a need..."
                        className="bg-emerald-900/20 border-emerald-700/30 text-white"
                      />
                    </div>
                  </div>

                  {/* Reveal Answer */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAnswers(prev => ({ ...prev, [`ex3-${item.id}`]: !prev[`ex3-${item.id}`] }))}
                    className="text-slate-400 hover:text-white"
                  >
                    {showAnswers[`ex3-${item.id}`] ? (
                      <><EyeOff className="w-4 h-4 mr-2" /> Hide Answer</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" /> Show Answer</>
                    )}
                  </Button>

                  {showAnswers[`ex3-${item.id}`] && (
                    <div className="mt-3 bg-emerald-900/30 border border-emerald-500/30 rounded-lg p-4 animate-in fade-in">
                      <p className="text-emerald-200">
                        <span className="text-slate-400">{item.feelingPrefix}</span>{' '}
                        <span className="text-rose-300 font-semibold">{item.answer.feeling}</span>{' '}
                        <span className="text-slate-400">{item.needPrefix}</span>{' '}
                        <span className="text-emerald-300 font-semibold">{item.answer.need}</span>
                        {item.needSuffix && <span className="text-slate-400"> {item.needSuffix}</span>}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      // EXERCISE 3 ANSWERS - Page 112
      case 8:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise3-answers">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <h2 className="text-3xl font-bold text-white mb-2">Exercise 3: Possible Answers</h2>
                <p className="text-emerald-300 text-lg">Page 112</p>
              </div>
              <FeelingsNeedsCTA />
            </div>

            <p className="text-slate-400 text-center mb-6">Click each item to reveal the possible answer</p>

            <div className="grid gap-4">
              {exercise3Data.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:border-emerald-500/30 transition-colors cursor-pointer"
                  onClick={() => setShowAnswers(prev => ({ ...prev, [`answer-${item.id}`]: !prev[`answer-${item.id}`] }))}
                >
                  <div className="flex items-start gap-3">
                    <span className="bg-amber-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      {item.id}
                    </span>
                    <div className="flex-1">
                      <p className="text-slate-300 mb-2">"{item.statement}"</p>
                      {showAnswers[`answer-${item.id}`] !== false && (
                        <div className="bg-emerald-900/20 border border-emerald-500/20 rounded-lg p-3 animate-in fade-in">
                          <p className="text-emerald-200">
                            <span className="text-slate-400">{item.feelingPrefix}</span>{' '}
                            <span className="text-rose-300 font-semibold">{item.answer.feeling}</span>{' '}
                            <span className="text-slate-400">{item.needPrefix}</span>{' '}
                            <span className="text-emerald-300 font-semibold">{item.answer.need}</span>
                            {item.needSuffix && <span className="text-slate-400"> {item.needSuffix}</span>}
                          </p>
                        </div>
                      )}
                    </div>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showAnswers[`answer-${item.id}`] === false ? '' : 'rotate-180'}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // CELEBRATION READING - Page 111
      case 9:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="celebration-reading">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <h2 className="text-3xl font-bold text-white mb-2">Information in Celebration</h2>
                <p className="text-amber-300 text-lg">Page 111 • LaShelle Charde's Connection Gem</p>
              </div>
              <FeelingsNeedsCTA />
            </div>

            <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 border border-amber-500/30 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <ReadingIcon size={28} color="#F59E0B" />
                <h3 className="text-amber-300 font-bold text-xl">Connection Gem of the Week</h3>
              </div>

              <div className="space-y-6 text-slate-200 leading-relaxed">
                <p>
                  It's hard to emphasize enough how helpful <span className="text-amber-300 font-semibold">celebration of needs met</span> can be.
                </p>

                <p>
                  Couples often come to me wanting skills and understanding regarding their problems. I offer this and it helps and at the same time I notice that they are also meeting a lot of needs for each other, but missing out on the important information meeting needs provides.
                </p>

                <p>
                  This week a woman in a couple expressed a celebration. She said her partner was just more there, softer. I asked her to get specific about what he said or did that gave her that sense. It took a few minutes before she could remember what happened. Finally she said to her partner, <span className="text-amber-200 italic">"This week I noticed that when I said something as you were passing by you stopped and turned to look at me as I talked."</span>
                </p>

                <p>
                  I then asked her to name the needs that were met when he did this. The list was long: <span className="text-emerald-300 font-semibold">support, collaboration, communion, being heard, connection, and love</span>.
                </p>

                <p>
                  Her partner was surprised hearing how many needs got met in his simple willingness to stop and listen when she spoke. I offered that knowing what simple things they do that meets needs is an <span className="text-amber-300 font-semibold">invaluable resource</span> in times of stress when other more elaborate ways of loving each other aren't happening.
                </p>

                <p>
                  In addition, identifying and celebrating these simple things daily builds a <span className="text-emerald-300 font-semibold">foundation of connection and partnership</span> every day.
                </p>
              </div>
            </div>

            <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-xl p-6">
              <h4 className="text-emerald-300 font-bold mb-4">🦒 Simple Street Giraffe Ways to Celebrate</h4>
              <ul className="space-y-3 text-slate-200">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>"Thanks, that gives me a sense of <span className="text-emerald-300">support and caring</span>."</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>"I sure do soak up that affection. It helps me feel <span className="text-emerald-300">relaxed</span>."</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>"I really get that you hear me when you say it back like that."</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>"Wow, you remembered I needed a bag lunch tomorrow. I feel so <span className="text-emerald-300">taken care of</span>."</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  <span>"When you get home and come straight to find me for a hug, my heart fills up cuz I know I am <span className="text-emerald-300">loved</span>."</span>
                </li>
              </ul>
            </div>

            <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-6">
              <h4 className="text-purple-300 font-bold mb-3">🌟 This Week's Challenge</h4>
              <p className="text-slate-200">
                Challenge yourself to do at least <span className="text-purple-300 font-semibold">one celebration of needs met</span> with someone close to you. 
                Remember it can be as simple as one sentence like the examples above. The most important part is to include:
              </p>
              <ul className="mt-3 space-y-2 text-slate-300">
                <li>• The <span className="text-blue-300">specific behavior</span></li>
                <li>• The <span className="text-emerald-300">needs it met</span> for you</li>
                <li>• The <span className="text-rose-300">feelings</span> that were present</li>
              </ul>
            </div>

            <p className="text-slate-500 text-sm text-center">
              From LaShelle Charde's Connection Gem of the Week • www.wiseheartpdx.org
            </p>
          </div>
        );

      // HOMEWORK - Page 113
      case 10:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="chapter4-homework">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-500/20">
                  <HomeworkIcon size={40} color="#ffffff" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Chapter 4 Homework</h2>
                <p className="text-indigo-300 text-lg">Page 113</p>
              </div>
              <FeelingsNeedsCTA />
            </div>

            <div className="space-y-6">
              {/* Reading Assignment */}
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <ReadingIcon size={24} color="#60A5FA" />
                  <h3 className="text-xl font-bold text-blue-300">Reading</h3>
                </div>
                <ul className="space-y-3 text-slate-200">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400">•</span>
                    <span>Read page 111 in this notebook, <span className="text-blue-300 font-medium">"Information in Celebration"</span></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400">•</span>
                    <span>Read <span className="text-blue-300 font-medium">Chapter 6</span> in Nonviolent Communication: A Language of Life: "Requesting That Which Would Enrich Life"</span>
                  </li>
                </ul>
              </div>

              {/* Video Assignment */}
              <div className="bg-rose-900/20 border border-rose-700/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <VideoIcon size={24} color="#FB7185" />
                  <h3 className="text-xl font-bold text-rose-300">Watch Video</h3>
                </div>
                <p className="text-slate-200 mb-3">
                  Part 3 of the NVC basics video: <span className="text-rose-300 font-medium">"Empathically Hearing Others"</span>
                </p>
                <p className="text-slate-400 text-sm">
                  Time marker <span className="text-rose-300 font-mono">1:30:41 - 2:23:20</span> on the YouTube version
                </p>
                <Button
                  onClick={() => window.open('https://www.youtube.com/watch?v=l7TONauJGfc&t=5441s', '_blank')}
                  variant="outline"
                  className="mt-4 border-rose-600 text-rose-300 hover:bg-rose-900/30"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Watch on YouTube
                </Button>
              </div>

              {/* Partner Exercise */}
              <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <PartnerIcon size={24} color="#34D399" />
                  <h3 className="text-xl font-bold text-emerald-300">Partner Exercises</h3>
                </div>
                
                <div className="bg-slate-800/50 rounded-lg p-5 mb-4">
                  <h4 className="text-white font-bold mb-3">Exercise: 10-Minute Sharing</h4>
                  <p className="text-slate-200 mb-4">
                    The speaker will talk for 10 minutes about either all or some of the following:
                  </p>
                  <ul className="space-y-2 text-slate-300 mb-4">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>The high point and/or low points of the last seven days</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>The high point and/or low points of your current relationship</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>The high point and/or low points with your best friend</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>The high point and/or low points of you dealing with your parents</span>
                    </li>
                  </ul>
                  
                  <div className="bg-emerald-900/30 border border-emerald-500/20 rounded-lg p-4">
                    <p className="text-emerald-200 text-sm">
                      <span className="font-semibold">Listener's Role:</span> Listen for feelings and needs being expressed either directly or in the form of judgments. When hearing a feeling, interject when it feels right and ask: <span className="italic">"Are you feeling ___ because you were needing ___?"</span>
                    </p>
                    <p className="text-emerald-300 text-sm mt-2">
                      The speaker will confirm or clarify whether guesses were correct and then continue to talk. <span className="font-semibold">Switch roles after 10 minutes.</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300 text-lg mb-4">
                You've completed <span className="text-emerald-400 font-bold">Chapter 4: Needs</span>! 🎉
              </p>
              <Button
                onClick={() => navigate('/belonging-ebook')}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white"
              >
                <ChapterIcon size={18} className="mr-2" />
                Return to eBook Contents
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${isPresentationMode ? 'overflow-hidden' : ''}`}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeftIcon size={16} className="mr-1" />
                Back
              </Button>
              <div className="h-5 w-px bg-slate-700" />
              <div>
                <h1 className="text-lg font-bold text-white">Chapter 4: Needs</h1>
                <p className="text-xs text-slate-400">Pages 108-113</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Sync Status */}
              {isLoggedIn && (
                <div className="flex items-center gap-1 text-xs text-slate-400" title={lastSyncTime ? `Last synced: ${lastSyncTime.toLocaleTimeString()}` : 'Not synced yet'}>
                  {isSyncing ? (
                    <Cloud className="w-4 h-4 animate-pulse text-blue-400" />
                  ) : lastSyncTime ? (
                    <Cloud className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <CloudOff className="w-4 h-4 text-slate-500" />
                  )}
                </div>
              )}
              
              <Button onClick={saveProgress} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <SaveIcon size={16} className="mr-1" />
                Save
              </Button>
              <Button onClick={downloadPDF} variant="ghost" size="sm" className="text-slate-400 hover:text-white" title="Download as PDF">
                <Download className="w-4 h-4 mr-1" />
                PDF
              </Button>
              <Button onClick={resetAll} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button onClick={togglePresentationMode} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                {isPresentationMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress value={progress_pct} className="h-1.5 bg-slate-700" />
            <div className="flex justify-between mt-1 text-xs text-slate-500">
              <span>Section {currentSection + 1} of {sections.length}</span>
              <span>Page {sections[currentSection]?.page}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Section Navigation (Index) */}
      {!isPresentationMode && (
        <div className="bg-slate-800/50 border-b border-slate-700/30">
          <div className="max-w-5xl mx-auto px-4 py-2">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-600">
              {sections.map((section, idx) => (
                <button
                  key={section.id}
                  onClick={() => setCurrentSection(idx)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${
                    currentSection === idx
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-slate-200'
                  }`}
                >
                  <span className="font-mono mr-1">p.{section.page}</span>
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`max-w-4xl mx-auto px-4 py-8 ${isPresentationMode ? 'h-screen flex items-center' : ''}`}>
        <div className="w-full">
          {renderContent()}
        </div>
      </main>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-700/50 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            onClick={() => setCurrentSection(prev => Math.max(prev - 1, 0))}
            disabled={currentSection === 0}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-30"
          >
            <ArrowLeftIcon size={18} className="mr-2" />
            Previous
          </Button>
          
          <div className="text-center">
            <span className="text-slate-400 text-sm">{sections[currentSection]?.title}</span>
          </div>
          
          <Button
            onClick={() => setCurrentSection(prev => Math.min(prev + 1, sections.length - 1))}
            disabled={currentSection === sections.length - 1}
            className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-30"
          >
            Next
            <ArrowRightIcon size={18} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Floating Feelings & Needs Helper */}
      <FloatingFeelingsHelper />

      {/* Feelings & Needs Helper Modal */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />

      {/* Page Footer (only on last section) */}
      {currentSection === sections.length - 1 && !isPresentationMode && (
        <div className="max-w-4xl mx-auto px-4 pb-24">
          <EbookPageFooter pageContext="chapter4_needs" pageNumber={113} />
        </div>
      )}
    </div>
  );
}
